import React from 'react';

import Layout from '../components/Layout';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PricingPlans from '../components/PricingPlans';

const GROUPS_COLLECTION_URL = 'https://help.erg.zone/category/113-groups';
const GROUPS_FAQ_URL = 'https://help.erg.zone/article/255-group-faq';
const GROUPS_START_HERE_URL =
  'https://help.erg.zone/article/295-groups-start-here';
const WEB_ADMIN_URL = 'https://admin.erg.zone/';

const sectionDarkStyle = {
  background: '#333333',
  backgroundImage: 'none',
};

const sectionDarkerStyle = {
  background: '#111111',
  backgroundImage: 'none',
};

const openGroupHelpBeacon = (e) => {
  if (e && e.preventDefault) e.preventDefault();
  if (typeof window !== 'undefined' && window.Beacon) {
    window.Beacon('prefill', {
      subject: 'Group Help',
      text: '',
    });
    window.Beacon('navigate', '/ask/message');
    window.Beacon('open');
  }
};

const audiences = [
  {
    icon: 'fas fa-user-tie',
    title: 'Coaches & Personal Trainers',
    description:
      'Easily deliver custom programming, monitor athlete performance, and manage your client base from a single, powerful web admin dashboard.',
  },
  {
    icon: 'fas fa-dumbbell',
    title: 'Gyms, Studios & Functional Fitness Affiliates',
    description:
      'Run classes more efficiently. Use the Live Leaderboard to display real-time member data on big screens to boost engagement and intensity.',
  },
  {
    icon: 'fas fa-users',
    title: 'Clubs & Competitive Teams',
    description:
      'Share team workouts, build camaraderie with private leaderboards, and coordinate training cycles seamlessly across your entire roster.',
  },
  {
    icon: 'fas fa-heart',
    title: 'Friends, Families & Small Circles',
    description:
      "Create a private space to hold each other accountable, share favorite workouts, and see each other's progress—whether you're in the same room or miles apart.",
  },
];

const features = [
  {
    icon: 'fas fa-bullseye',
    title: 'Workout Consistency',
    description:
      'Ensure your programming is followed exactly as you intended. ErgZone keeps athletes on track with your specific intervals and targets, reducing confusion and manual setup errors.',
  },
  {
    icon: 'fas fa-users-cog',
    title: 'Group Management',
    description:
      "Take full control of your community. Use the Web Admin or the App to organize your roster, manage memberships, and deploy content to your group's feed instantly.",
  },
  {
    icon: 'fas fa-tv',
    title: 'Live Dashboard',
    description:
      "Monitor your athletes' real-time Pace, Cadence, Watts, and any other metric available on ErgZone. Stream the dashboard to a large display for interactive group classes or use it to power live virtual races.",
  },
  {
    icon: 'fas fa-trophy',
    title: 'Leaderboards',
    description:
      'Enable friendly competition. Group members can view results, filter standings, and compare performance directly inside the app with verified data.',
  },
  {
    icon: 'fas fa-file-export',
    title: 'Data Export',
    description:
      'Go deeper into the numbers. Download group results and performance data to analyze athlete progress in spreadsheets or third-party coaching tools.',
  },
  {
    icon: 'fas fa-comments',
    title: 'In-App Chat',
    description:
      'Keep members motivated, share strategy, and give direct feedback without leaving the ErgZone platform.',
  },
];

const steps = [
  {
    icon: 'fas fa-layer-group',
    title: 'Choose a Tier',
    description: (
      <>
        Visit the{' '}
        <a
          href={WEB_ADMIN_URL}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: '#00c9be', textDecoration: 'underline' }}
        >
          ErgZone Web Admin
        </a>{' '}
        and choose the plan that fits your community size.
      </>
    ),
  },
  {
    icon: 'fas fa-plus-circle',
    title: 'Create Your Group',
    description:
      'Set up the group with a logo, description, and your preferred privacy settings.',
  },
  {
    icon: 'fas fa-paper-plane',
    title: 'Invite Members',
    description:
      'Share the unique group link with athletes and partners to get them into the action.',
  },
  {
    icon: 'fas fa-clipboard-list',
    title: 'Program Workouts',
    description:
      "Build the workout library on the web or in-app and publish sessions to the group's feed.",
  },
  {
    icon: 'fas fa-chart-line',
    title: 'Track & Analyze',
    description:
      'Watch live results, manage leaderboards, and export performance data for deep analysis.',
  },
];

const GroupsPage = () => (
  <Layout titlePrefixes={['Groups, Clubs, Teams & Studios']}>
    <style>{`
      .section-dark .section-heading h2,
      .section-dark .section-heading p,
      .section-dark h3,
      .section-dark h4,
      .section-dark p,
      .section-dark .text-muted,
      .section-dark .lead { color: rgba(255, 255, 255, 0.95); }
      .section-dark .feature-item h3,
      .section-dark .feature-item p { color: rgba(255, 255, 255, 0.9); }
      .section-dark .feature-item i { color: #00c9be !important; }
      .section-dark hr { border-color: rgba(255, 255, 255, 0.3); }

      /* Recolor the feature-item icon gradient with a much wider range —
         from a mint-teal highlight on the right, through the brand teal
         in the middle, down to a near-black deep teal on the left. Keeps
         the icons on brand while giving them dramatic depth. */
      .feature-item i {
        background: -webkit-linear-gradient(to left, #9FF4EB 0%, #00c9be 40%, #00504c 75%, #001210 100%) !important;
        background: linear-gradient(to left, #9FF4EB 0%, #00c9be 40%, #00504c 75%, #001210 100%) !important;
        -webkit-background-clip: text !important;
        background-clip: text !important;
        -webkit-text-fill-color: transparent !important;
      }

    `}</style>
    <Header />

    <header className="masthead text-center" id="top">
      <div className="container">
        <div className="row h-100">
          <div className="col-lg-7 my-auto">
            <div className="header-content mx-auto">
              <h1 className="mb-4">
                ErgZone Groups: Clubs, Teams, Studios &amp; Gyms
              </h1>
              <p className="lead mb-4">
                Streamline your programming, engage your members, and track data
                effortlessly—whether you manage a local studio, a competitive
                team, or a remote coaching client base.
              </p>
              <div className="d-flex flex-column flex-sm-row">
                <a
                  href={WEB_ADMIN_URL}
                  className="btn btn-outline btn-xl mr-sm-3 mb-2 mb-sm-0 plausible-event-name=CreateGroup+Click"
                  style={{ whiteSpace: 'nowrap' }}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Create a Group
                </a>
                <a
                  href={GROUPS_COLLECTION_URL}
                  className="btn btn-outline btn-xl"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View Documentation
                </a>
              </div>
            </div>
          </div>
          <div className="col-lg-5 my-auto">
            <div className="device-container">
              <div
                style={{
                  position: 'relative',
                  aspectRatio: '9/16',
                  maxWidth: '325px',
                  margin: '0 auto',
                  overflow: 'hidden',
                  borderRadius: '12px',
                  backgroundColor: 'rgba(0,0,0,0.35)',
                }}
              >
                <iframe
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    borderRadius: '12px',
                  }}
                  src="https://www.youtube.com/embed/MsPY2D59fpQ?rel=0"
                  referrerPolicy="strict-origin-when-cross-origin"
                  title="ErgZone Groups - Live Dashboard"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  frameBorder="0"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>

    <section className="features" id="shared-experience">
      <div className="container">
        <div className="section-heading text-center">
          <h2>A Shared Experience: Anywhere, Any Time.</h2>
          <p className="text-muted">
            ErgZone Groups provide a dedicated digital space for any community
            to train together.
          </p>
          <hr />
        </div>
        <div className="row">
          <div className="col-lg-10 mx-auto">
            <p className="text-center lead">
              Whether it&apos;s family members or friends following the same
              plan in a home gym, a local class hitting a PR together, or a
              competitive team training from different cities, Groups bring
              everyone&apos;s data into one unified view—no matter when or where
              you choose to train.
            </p>
          </div>
        </div>
      </div>
    </section>

    <section
      className="features section-dark"
      id="who-its-for"
      style={sectionDarkerStyle}
    >
      <div className="container">
        <div className="section-heading text-center">
          <h2>Who is ErgZone Groups For?</h2>
          <p className="text-muted">
            One platform built for every kind of training community.
          </p>
          <hr />
        </div>
        <div className="row">
          {audiences.map((audience) => (
            <div className="col-lg-6" key={audience.title}>
              <div className="feature-item">
                <i className={`${audience.icon} text-primary`}></i>
                <h3>{audience.title}</h3>
                <p className="text-muted">{audience.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="features" id="features">
      <div className="container">
        <div className="section-heading text-center">
          <h2>Key Features &amp; Benefits</h2>
          <p className="text-muted">
            Everything you need to program, motivate, and measure—built right
            in.
          </p>
          <hr />
        </div>
        <div className="row justify-content-center">
          {features.map((feature) => (
            <div className="col-lg-4 col-md-6" key={feature.title}>
              <div className="feature-item">
                <i className={feature.icon}></i>
                <h3>{feature.title}</h3>
                <p className="text-muted">{feature.description}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="row">
          <div className="col-lg-10 mx-auto text-center mt-3">
            <p className="text-muted" style={{ marginBottom: 0 }}>
              <i
                className="fas fa-store"
                style={{
                  color: '#00c9be',
                  marginRight: '8px',
                  background: 'none',
                  WebkitTextFillColor: '#00c9be',
                }}
              ></i>
              Ready to grow beyond your private group? Seamlessly transition
              into selling your programming on the{' '}
              <a
                href="https://help.erg.zone/article/108-selling-content-on-ergzone"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#00c9be', textDecoration: 'underline' }}
              >
                ErgZone Marketplace
              </a>
              .
            </p>
          </div>
        </div>
      </div>
    </section>

    <section
      className="features section-dark"
      id="how-it-works"
      style={sectionDarkStyle}
    >
      <div className="container">
        <div className="section-heading text-center">
          <h2>How It Works</h2>
          <p className="text-muted">
            Start delivering programming and tracking performance in five simple
            steps.
          </p>
          <hr />
        </div>
        <div className="row">
          {steps.map((step) => (
            <div className="col-12 col-md-6 col-lg" key={step.title}>
              <div className="feature-item">
                <i className={step.icon}></i>
                <h3>{step.title}</h3>
                <p className="text-muted">{step.description}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="row">
          <div className="col-lg-10 mx-auto text-center mt-3">
            <p className="text-muted" style={{ marginBottom: 0 }}>
              <i
                className="fas fa-book-open"
                style={{
                  color: '#00c9be',
                  marginRight: '8px',
                  background: 'none',
                  WebkitTextFillColor: '#00c9be',
                }}
              ></i>
              Need a detailed walkthrough? Check out our{' '}
              <a
                href={GROUPS_START_HERE_URL}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#00c9be', textDecoration: 'underline' }}
              >
                Step-by-Step Guide
              </a>{' '}
              to see exactly how to set up your group.
            </p>
          </div>
        </div>
      </div>
    </section>

    <PricingPlans groupsOnly />

    <section
      className="features section-dark"
      id="faq"
      style={sectionDarkerStyle}
    >
      <div className="container">
        <div className="section-heading text-center">
          <h2>Frequently Asked Questions</h2>
          <hr />
        </div>

        <div className="row">
          <div className="col-lg-10 mx-auto">
            <div className="mb-4">
              <h4 className="mb-2">
                Do members need a paid account or subscription to join?
              </h4>
              <p className="text-muted">
                No. Members can join your group and access group workouts using
                the free version of the ErgZone app—your subscription as the
                group owner covers their standard participation. However,
                individual members can choose to purchase an ErgZone+
                subscription on their own if they want to unlock all premium
                ErgZone features for their personal training.
              </p>
            </div>

            <div className="mb-4">
              <h4 className="mb-2">
                Can I get advanced performance metrics for my athletes?
              </h4>
              <p className="text-muted">
                Yes. Group owners can upgrade to our Crew Package to unlock
                expanded tracking for their team. Paid for by the group owner,
                this upgrade includes extra metrics, advanced graphs, and heart
                rate connectivity directly through the app (note: it does not
                include third-party or external integrations). Contact us at{' '}
                <a
                  href="mailto:support@erg.zone"
                  style={{ color: '#00c9be', textDecoration: 'underline' }}
                >
                  support@erg.zone
                </a>{' '}
                to set up this upgrade for your roster.
              </p>
            </div>

            <div className="mb-4">
              <h4 className="mb-2">How do members join my group?</h4>
              <p className="text-muted">
                Each group has a unique link you can share via text, email, or
                social media. When members tap the link, they will be prompted
                to join your group. Depending on your settings, they will either
                be joined automatically or wait for your approval.
              </p>
            </div>

            <div className="mb-4">
              <h4 className="mb-2">Can I keep my group private?</h4>
              <p className="text-muted">
                Yes. You decide if your group is discoverable in search or
                completely hidden. You also control whether members can join
                automatically or require your approval.{' '}
                <em>
                  Note: We generally recommend requiring approval so you can
                  manage your roster and billing effectively.
                </em>
              </p>
            </div>

            <div className="mb-4">
              <h4 className="mb-2">
                What happens if I need to add more athletes than my current plan
                covers?
              </h4>
              <p className="text-muted">
                You can scale your roster at any time! If you add more athletes
                than your current plan allows, the system will prompt you to
                upgrade your tier. Your account will automatically calculate a
                prorated amount for the remainder of your billing cycle to cover
                the extra athletes immediately, ensuring no disruption to your
                training or communication.
              </p>
            </div>

            <div className="mb-4">
              <h4 className="mb-2">Can I switch tiers or cancel my plan?</h4>
              <p className="text-muted">
                Absolutely. You can upgrade, downgrade, or cancel via the{' '}
                <a
                  href={WEB_ADMIN_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: '#00c9be', textDecoration: 'underline' }}
                >
                  ErgZone Web Admin
                </a>{' '}
                at any time. There are no long-term contracts.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section
      className="bg-primary text-center"
      id="cta"
      style={{ paddingTop: '60px', paddingBottom: '80px' }}
    >
      <div className="container">
        <div className="row">
          <div className="col-md-8 mx-auto">
            <h2 className="section-heading" style={{ marginBottom: '32px' }}>
              Start Your Group Today
            </h2>
            <div style={{ marginBottom: '40px' }}>
              <a
                href={WEB_ADMIN_URL}
                className="btn btn-light btn-xl plausible-event-name=CreateGroup+Click"
                target="_blank"
                rel="noopener noreferrer"
              >
                Get Started
              </a>
            </div>

            <p style={{ marginBottom: '16px' }}>
              Prefer to read up first? Browse the full Groups help library for
              setup guides and FAQs.
            </p>
            <div style={{ marginBottom: '28px' }}>
              <a
                href={GROUPS_COLLECTION_URL}
                className="btn btn-outline btn-xl mr-3 mb-2 mb-md-0"
                target="_blank"
                rel="noopener noreferrer"
              >
                Group Documentation
              </a>
              <a
                href={GROUPS_FAQ_URL}
                className="btn btn-outline btn-xl"
                target="_blank"
                rel="noopener noreferrer"
              >
                Pricing FAQ
              </a>
            </div>

            <p style={{ marginTop: '8px', paddingBottom: '24px' }}>
              Have questions?{' '}
              <a
                href="#"
                style={{ color: 'inherit', textDecoration: 'underline' }}
                onClick={openGroupHelpBeacon}
              >
                Contact us
              </a>{' '}
              and we&apos;ll help you plan your launch.
            </p>
          </div>
        </div>
      </div>
    </section>

    <Footer />
  </Layout>
);

export default GroupsPage;
